import { TableColumn } from '@backstage/core-components';
import {
  CatalogTable,
  CatalogTableColumnsFunc,
  CatalogTableRow,
} from '@backstage/plugin-catalog';

const clusterOf = ({ entity }: CatalogTableRow) =>
  entity.metadata.annotations?.['backstage.io/kubernetes-cluster'] ?? '';

// Deployments are titled <release> @ <namespace>, so one release in one
// namespace on two clusters shares a title; this column tells them apart.
const clusterColumn: TableColumn<CatalogTableRow> = {
  title: 'Cluster',
  render: row => clusterOf(row),
  customSort: (a, b) => clusterOf(a).localeCompare(clusterOf(b)),
  customFilterAndSearch: (query, row) =>
    clusterOf(row)
      .toLocaleLowerCase('en-US')
      .includes(String(query).toLocaleLowerCase('en-US')),
  cellStyle: { whiteSpace: 'nowrap' },
};

// Short values that read badly when broken across lines.
const ONE_LINE_FIELDS = [
  'resolved.partOfSystemRelationTitle',
  'resolved.ownedByRelationsTitle',
  'entity.spec.type',
  'entity.spec.lifecycle',
];

/** Room for a deployment's <release> @ <namespace> beside the Cluster column. */
function readable(
  column: TableColumn<CatalogTableRow>,
): TableColumn<CatalogTableRow> {
  if (column.field === 'resolved.entityRef') {
    return {
      ...column,
      headerStyle: { minWidth: 360 },
      cellStyle: { minWidth: 360 },
    };
  }
  return ONE_LINE_FIELDS.includes(column.field ?? '')
    ? { ...column, cellStyle: { whiteSpace: 'nowrap' } }
    : column;
}

/** The default catalog columns, plus Cluster after Name when listing Resources. */
export const catalogColumns: CatalogTableColumnsFunc = context => {
  const defaults = CatalogTable.defaultColumnsFunc(context);
  if (context.filters.kind?.value.toLocaleLowerCase('en-US') !== 'resource') {
    return defaults;
  }
  const columns = defaults.map(readable);
  const name = columns.findIndex(
    column => column.field === 'resolved.entityRef',
  );
  return name === -1
    ? [...columns, clusterColumn]
    : [
        ...columns.slice(0, name + 1),
        clusterColumn,
        ...columns.slice(name + 1),
      ];
};
